import { useCallback, useState } from 'react';

import { AXIOS_INSTANCE, type ParamsType } from '@/api/instance';
import { triggerBlobDownload } from '@/lib/download';

export interface UseFileDownloadOptions<T> {
  url: string;
  filename: string;
  params?: T;
  onError?: (error: unknown) => void;
}

export const useFileDownload = <T extends ParamsType = ParamsType>({
  url,
  filename,
  params,
  onError,
}: UseFileDownloadOptions<T>): {
  download: () => Promise<void>;
  isDownloading: boolean;
} => {
  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(async () => {
    if (!url) {
      return;
    }

    setIsDownloading(true);
    try {
      const response = await AXIOS_INSTANCE.get<Blob>(url, {
        params,
        responseType: 'blob',
      });
      triggerBlobDownload(response.data, filename);
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        throw error;
      }
    } finally {
      setIsDownloading(false);
    }
  }, [url, filename, params, onError]);

  return { download, isDownloading };
};
